import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { CartUpdateEvent, ThemeEvents, VariantSelectedEvent } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';
import VariantPicker from '@theme/variant-picker';

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();
  /** @type {AbortController} */
  #cartUpdateAbortController = new AbortController();

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    const hotspotProduct = /** @type {import('./product-hotspot').ProductHotspotComponent | null} */ (
      this.closest('product-hotspot-component')
    );
    const productLink = productCard?.getProductCardLink() || hotspotProduct?.getHotspotProductLink();

    if (!productLink?.href) return '';

    const url = new URL(productLink.href);

    if (url.searchParams.has('variant')) {
      return url.toString();
    }

    const selectedVariantId = this.#getSelectedVariantId();
    if (selectedVariantId) {
      url.searchParams.set('variant', selectedVariantId);
    }

    return url.toString();
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() || null;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, {
      signal: this.#cartUpdateAbortController.signal,
    });
    document.addEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState.bind(this));
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#abortController?.abort();
    this.#cartUpdateAbortController.abort();
    document.removeEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState.bind(this));
  }

  /**
   * Clears the cached content when cart is updated
   */
  #handleCartUpdate = () => {
    this.#cachedContent.clear();
  };

  /**
   * Re-renders the variant picker in the quick-add modal.
   * @param {Element} newHtml - The element to re-render.
   */
  #updateVariantPicker(newHtml) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;
    const variantPicker = /** @type {VariantPicker} */ (modalContent.querySelector('variant-picker'));
    variantPicker.updateVariantPicker(newHtml);
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    // dm-personalized products personalize inline on their PDP — never in the modal
    // hub. Two parallel personalization interfaces must not exist (design-sync-sop A6.6).
    if (this.dataset.quickAddMode === 'link') {
      if (this.productPageUrl) window.location.href = this.productPageUrl;
      return;
    }

    if (this.dataset.quickAddMode === 'personalization') {
      await this.#openPersonalizationModal();
      return;
    }

    const currentUrl = this.productPageUrl;

    // Check if we have cached content for this URL
    let productGrid = this.#cachedContent.get(currentUrl);

    if (!productGrid) {
      // Fetch and cache the content
      const html = await this.fetchProductPage(currentUrl);
      if (html) {
        const gridElement = html.querySelector('[data-product-grid-content]');
        if (gridElement) {
          // Cache the cloned element to avoid modifying the original
          productGrid = /** @type {Element} */ (gridElement.cloneNode(true));
          this.#cachedContent.set(currentUrl, productGrid);
        }
      }
    }

    if (productGrid) {
      // Use a fresh clone from the cache
      const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
      await this.updateQuickAddModal(freshContent);
      this.#updateVariantPicker(productGrid);
    }

    this.#openQuickAddModal();
  };

  async #openPersonalizationModal() {
    const currentUrl = this.productPageUrl;
    let personalizationContent = this.#cachedContent.get(`personalization:${currentUrl}`);

    if (!personalizationContent) {
      const html = await this.fetchProductPage(currentUrl);
      if (html) {
        personalizationContent = this.#buildPersonalizationContent(html, currentUrl);
        if (personalizationContent) {
          this.#cachedContent.set(`personalization:${currentUrl}`, personalizationContent);
        }
      }
    }

    if (!personalizationContent) {
      if (currentUrl) window.location.href = currentUrl;
      return;
    }

    const host = document.getElementById('quick-add-personalization-content');
    if (!host) {
      if (currentUrl) window.location.href = currentUrl;
      return;
    }

    host.replaceChildren(personalizationContent.cloneNode(true));
    await customElements.whenDefined('wroe-personalization-component');

    const personalizationComponent = /** @type {HTMLElement & { handleLauncherClick?: (event: Event) => void; showDialog?: () => void }} */ (
      host.querySelector('wroe-personalization-component')
    );

    this.toggleAttribute('stay-visible', true);

    const dialog = personalizationComponent?.querySelector('dialog');
    dialog?.addEventListener('close', () => this.toggleAttribute('stay-visible', false), { once: true });

    if (typeof personalizationComponent?.handleLauncherClick === 'function') {
      personalizationComponent.handleLauncherClick(new Event('click'));
    } else if (typeof personalizationComponent?.showDialog === 'function') {
      personalizationComponent.showDialog();
    } else if (currentUrl) {
      window.location.href = currentUrl;
    }
  }

  /**
   * @param {Document} html
   * @param {string} productPageUrl
   * @returns {DocumentFragment | null}
   */
  #buildPersonalizationContent(html, productPageUrl) {
    const personalizationComponent = html.querySelector('wroe-personalization-component');
    if (!personalizationComponent) return null;

    const fragment = document.createDocumentFragment();
    const idSuffix = this.#getPersonalizationIdSuffix(productPageUrl);
    const stepsConfigId = `quick-add-wroe-steps-${idSuffix}`;
    const previewConfigId = `quick-add-wroe-preview-config-${idSuffix}`;

    for (const configScript of html.querySelectorAll('script[data-wroe-steps], script[data-wroe-preview-config]')) {
      const clonedScript = /** @type {HTMLScriptElement} */ (configScript.cloneNode(true));
      if (clonedScript.matches('[data-wroe-steps]')) clonedScript.id = stepsConfigId;
      if (clonedScript.matches('[data-wroe-preview-config]')) clonedScript.id = previewConfigId;
      fragment.appendChild(clonedScript);
    }

    const existingStyle = document.querySelector('style[data-quick-add-personalization-style]');
    if (!existingStyle) {
      for (const style of html.querySelectorAll('style')) {
        if (!style.textContent?.includes('wroe-personalization-modal')) continue;

        const clonedStyle = /** @type {HTMLStyleElement} */ (style.cloneNode(true));
        clonedStyle.dataset.quickAddPersonalizationStyle = 'true';
        document.head.appendChild(clonedStyle);
      }
    }

    const clonedComponent = /** @type {HTMLElement} */ (personalizationComponent.cloneNode(true));
    const selectedVariantId = new URL(productPageUrl).searchParams.get('variant');
    clonedComponent.id = `QuickAddWroePersonalization-${idSuffix}`;
    clonedComponent.dataset.stepsConfigId = stepsConfigId;
    clonedComponent.dataset.previewConfigId = previewConfigId;
    if (selectedVariantId) clonedComponent.dataset.selectedVariantId = selectedVariantId;

    const title = clonedComponent.querySelector('[id^="wroePersonalizationTitle-"]');
    const dialog = clonedComponent.querySelector('dialog');
    if (title instanceof HTMLElement && dialog instanceof HTMLDialogElement) {
      title.id = `quick-add-wroe-personalization-title-${idSuffix}`;
      dialog.setAttribute('aria-labelledby', title.id);
    }

    const petNameInput = clonedComponent.querySelector('[id^="WroePetName-"]');
    const petNameLabel = clonedComponent.querySelector('label[for^="WroePetName-"]');
    const petNameHelp = clonedComponent.querySelector('[id^="WroePetNameHelp-"]');
    if (petNameInput instanceof HTMLInputElement) {
      petNameInput.id = `quick-add-wroe-pet-name-${idSuffix}`;
      if (petNameLabel instanceof HTMLLabelElement) petNameLabel.htmlFor = petNameInput.id;
      if (petNameHelp instanceof HTMLElement) {
        petNameHelp.id = `quick-add-wroe-pet-name-help-${idSuffix}`;
        petNameInput.setAttribute('aria-describedby', petNameHelp.id);
      }
    }

    fragment.appendChild(clonedComponent);
    return fragment;
  }

  /**
   * @param {string} value
   * @returns {string}
   */
  #getPersonalizationIdSuffix(value) {
    let hash = 0;
    for (let index = 0; index < value.length; index += 1) {
      hash = (hash << 5) - hash + value.charCodeAt(index);
      hash |= 0;
    }

    return Math.abs(hash).toString(36);
  }

  #resetScroll() {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    const productDetails = dialogComponent.querySelector('.product-details');
    const productMedia = dialogComponent.querySelector('.product-information__media');
    productDetails?.scrollTo({ top: 0, behavior: 'instant' });
    productMedia?.scrollTo({ top: 0, behavior: 'instant' });
  }

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();

    // is nondeterministic when the open attribute is set on the dialog element after .showDialog() is called.
    // Waiting until the open animation starts seemed to be the most reliable metric here.
    const dialog = dialogComponent.refs?.dialog;
    if (!dialog) return;
    dialog.addEventListener('animationstart', this.#resetScroll.bind(this), { once: true });
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      return html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    if (isMobileBreakpoint()) {
      const productDetails = productGrid.querySelector('.product-details');
      const productFormComponent = productGrid.querySelector('product-form-component');
      const variantPicker = productGrid.querySelector('variant-picker');
      const productPrice = productGrid.querySelector('product-price');
      const productTitle = document.createElement('a');
      productTitle.textContent = this.dataset.productTitle || '';

      // Make product title as a link to the product page
      productTitle.href = this.productPageUrl;

      const productHeader = document.createElement('div');
      productHeader.classList.add('product-header');

      productHeader.appendChild(productTitle);
      if (productPrice) {
        productHeader.appendChild(productPrice);
      }
      productGrid.appendChild(productHeader);

      if (variantPicker) {
        productGrid.appendChild(variantPicker);
      }
      if (productFormComponent) {
        productGrid.appendChild(productFormComponent);
      }

      productDetails?.remove();
    }

    morph(modalContent, productGrid);

    this.#syncVariantSelection(modalContent);
  }

  /**
   * Updates the quick-add button state based on whether a swatch is selected
   * @param {VariantSelectedEvent} event - The variant selected event
   */
  #updateQuickAddButtonState(event) {
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target.closest('product-card') !== this.closest('product-card')) return;
    if (this.dataset.quickAddMode === 'personalization') return;
    const productOptionsCount = this.dataset.productOptionsCount;
    const quickAddButton = productOptionsCount === '1' ? 'add' : 'choose';
    this.setAttribute('data-quick-add-button', quickAddButton);
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink);

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Closes the dialog
   * @param {CartUpdateEvent} event - The cart update event
   */
  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
  };

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector('.view-product-title a')
    );
    const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
    const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

    if (!anchorElement) return;

    if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
    if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
